import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Building2, Home, Package, ArrowRight, TrendingUp, Weight, DollarSign, BarChart3
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/buildings`;

export default function Dashboard() {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/dashboard`);
      setDashboard(response.data);
    } catch (error) {
      toast.error('خطأ في تحميل البيانات');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin" />
      </div>
    );
  }

  const stats = [
    { icon: Building2, label: 'إجمالي المشاريع', value: dashboard?.total_projects || 0, color: 'bg-purple-100 text-purple-600' },
    { icon: Home, label: 'إجمالي الشقق', value: dashboard?.total_units || 0, color: 'bg-blue-100 text-blue-600' },
    { icon: Weight, label: 'إجمالي الحديد (طن)', value: dashboard?.total_steel_tons?.toLocaleString() || 0, color: 'bg-amber-100 text-amber-600' },
    { icon: DollarSign, label: 'التكلفة التقديرية', value: `${(dashboard?.total_estimated_cost || 0).toLocaleString()} ريال`, color: 'bg-emerald-100 text-emerald-600' },
  ];

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800 flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-purple-600" />
            لوحة التحكم
          </h1>
          <p className="text-sm text-gray-500 mt-1">نظرة عامة على جميع المشاريع</p>
        </div>
        <Button onClick={() => navigate('/buildings')} variant="outline" className="gap-2">
          <ArrowRight className="w-4 h-4" />
          العودة
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {stats.map((stat, idx) => (
          <Card key={idx} className="border-0 shadow-md">
            <CardContent className="p-4">
              <div className={`w-10 h-10 ${stat.color} rounded-lg flex items-center justify-center mb-3`}>
                <stat.icon className="w-5 h-5" />
              </div>
              <p className="text-xs text-gray-500 mb-1">{stat.label}</p>
              <p className="text-lg font-bold text-gray-800">{stat.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Projects List */}
      <Card className="shadow-md border-0">
        <CardHeader className="bg-gradient-to-r from-purple-500 to-violet-600 text-white rounded-t-xl p-4">
          <CardTitle className="text-lg flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            ملخص المشاريع
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          {!dashboard?.projects_summary?.length ? (
            <div className="text-center py-8">
              <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 mb-4">لا توجد مشاريع بعد</p>
              <Button onClick={() => navigate('/buildings/projects')} className="bg-purple-600 hover:bg-purple-700">
                إنشاء مشروع جديد
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="p-3 text-right font-bold">المشروع</th>
                    <th className="p-3 text-center font-bold">الحالة</th>
                    <th className="p-3 text-center font-bold">الشقق</th>
                    <th className="p-3 text-center font-bold">المساحة</th>
                    <th className="p-3 text-center font-bold">الحديد (طن)</th>
                    <th className="p-3 text-center font-bold">التكلفة</th>
                    <th className="p-3 text-center font-bold">إجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboard.projects_summary.map((project) => (
                    <tr key={project.id} className="border-b hover:bg-gray-50">
                      <td className="p-3 font-medium">{project.name}</td>
                      <td className="p-3 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          project.status === 'مكتمل' ? 'bg-green-100 text-green-700' :
                          project.status === 'متوقف' ? 'bg-red-100 text-red-700' :
                          'bg-blue-100 text-blue-700'
                        }`}>
                          {project.status}
                        </span>
                      </td>
                      <td className="p-3 text-center">{project.units_count}</td>
                      <td className="p-3 text-center">{project.area?.toLocaleString()} م²</td>
                      <td className="p-3 text-center font-bold text-amber-600">{project.steel_tons}</td>
                      <td className="p-3 text-center font-bold text-emerald-600">{project.estimated_cost?.toLocaleString()}</td>
                      <td className="p-3 text-center">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => navigate(`/buildings/projects/${project.id}`)}
                          className="text-xs h-8"
                        >
                          تفاصيل
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
